import { OptionType } from "ng/src/app/core/Option";
import { Utils } from "ng/src/app/core/utils";
import { BasicRecord } from 'ng/src/app/shared/model/app.model';
import { TemplateSegment, TemplateSegmentTypeId } from 'ng/src/app/templates/shared/template.model';
import { TagValue } from "ng/src/app/tags/tag.model";

export interface TemplateValueCalculator {
	calculateDependencyGraph(segments: TemplateSegment[]): SegmentDependencyGraph;
	calculateValues(
		segmentGraph: SegmentDependencyGraph,
		segmentMap: SegmentMap,
		valueMap: TemplateValueCalculatorValueMap,
		errorMap: ErrorMap,
		selectedTagHrefs: SelectedTagMap,
		currentTagObjects: TagValue[],
		selectedLogId: number,
		timeZone: string,
		note: any
	): Promise<TemplateValueCalculatorValueMap>;
}

export type SegmentDependencyGraph = Array<TemplateValueCalculatorNode>;
export type SegmentMap = { [segmentId: number]: Partial<TemplateSegment> & Pick<TemplateSegment, 'id' | 'longName'> };

export type ErrorMap = { [segmentId: number]: string | undefined };
export type SelectedTagMap = { [segmentId: number]: string[] };
export type TemplateValueCalculatorValueMap = { [segmentId: number]: any } | unknown[];

export interface TemplateValueCalculatorNode {
	id: number;
	ts: TemplateSegment;
	optionalVars: { [segmentId: number]: boolean };
	variableMap: { [variableName: string]: number };
	dependsOn: number[];
	dependsOnExtValues: string[];
	isDependedOn: number[];
}

// Formula parsing and calculation
export namespace Formula {
	// Scope separator for TemplateSegment formula
	export const scopeSeparator = '### logbook variable declarations ###\n' as const;
	
	// Valid variable name pattern
	export const namePattern = /^[A-Za-z_]+$/;
	
	export const parsingPattern = /^(?<name>[A-Za-z_]+) = (?:\$id(?<id>\d+)|\$extValues\['(?<extValue>.+)'])(?<optional> # optional)?$/
	
	// Valid keys for external values
	export const ExtValues = {
		'noteId': 'Note ID',
		'noteTime': 'Note Time',
		'carryForward': 'Carry Forward',
		'carryForwardEndTime': 'Carry Forward End Time',
		'created': 'Created',
		'turnover': 'Turnover',
		'draft': 'Draft',
		'currentUserId': 'Current User ID'
	} as const;
	
	export type ExtValueKey = keyof typeof ExtValues;
	
	// Variable construct
	export type Variable = {
		name: string,
		reference: number | ExtValueKey,
		optional?: boolean,
		type: 'variable' | 'external'
	}
	
	// Scope that contains variables and formula
	export type Scope = {
		variables: Variable[],
		formula: string,
		decimalPlaces?: number | null
	}
	
	export function emptyScope(): Scope {
		return {
			variables: [],
			formula: ''
		}
	}
	
	export function getReferenceLabelForVariable(variable: Variable, segmentMap?: BasicRecord<TemplateSegment>) {
		if (variable.type === 'external') {
			return Formula.ExtValues[variable.reference as ExtValueKey];
		}
		
		const segment = segmentMap?.[variable.reference];
		
		if (segment == null) {
			return 'unknown';
		}
		
		return Utils.trimToNull(segment.longName) ?? segment.name ?? segment.type.name;
	}
	
	// Converts a scope to a formula string
	export function scopeToString(scope: Scope): string {
		return scope.variables.map(v => {
			const optionalStr = v.optional ? ' # optional' : '';
			const ref = `${v.reference}${optionalStr}`;
			switch (v.type) {
				case 'variable':
					return `${v.name} = $id${ref}`;
				case 'external':
					return `${v.name} = $extValues['${ref}']`;
			}
		}).join('\n') + scopeSeparator + scope.formula;
	}
	
  // Parses a formula string into a scope.
	// Throws an error if the formula is invalid
	export function parseFormula(segment: TemplateSegment): Scope {
		const segmentFormula = segment.formula;
		const scope: Scope = {
			variables: [],
			formula: ''
		};
		
		if (segment.type.id === TemplateSegmentTypeId.NUMERIC_INPUT) {
			scope.decimalPlaces = segment.decimalPlaces ?? null;
		}
		
		if ((segmentFormula?.length ?? 0) > 0) {
			const formulaSplit = segmentFormula?.split(scopeSeparator) ?? [];
			if (formulaSplit.length == 2) {
				const variableStr = formulaSplit[0];
				scope.variables = variableStr
					.split('\n')
					.filter(s => s.trim().length)
					.map(str => {
						const match = str.match(parsingPattern);
						
						if (match == null) {
							throw new Error(`Invalid variable declaration ${str}`);
						}
						
						const name = OptionType.nonNull(match.groups?.name).getOrThrow('Variable name is null');
						
						const reference = OptionType
							.nonNull(match.groups?.id)
							.match({
								some: (id) => OptionType.nonNull(Utils.parseNumberOrUndefined(id)).getOrThrow('Variable name is invalid ID'),
								none: () => OptionType.nonNull(match.groups?.extValue as ExtValueKey | undefined).getOrThrow('Variable name is null'),
							});
						
						const optional = match.groups?.optional != null;
						
						const type = OptionType.nonNull(match.groups?.id)
							.match({
								some: () => 'variable' as const,
								none: () => 'external' as const,
							});
						
						return {
							name,
							reference,
							optional,
							type
						};
					});
				
				scope.formula = formulaSplit[1];
			} else if (formulaSplit.length == 1) {
				scope.formula = formulaSplit[0];
			} else {
				throw new Error(`Invalid formula ${segmentFormula}}`);
			}
		}
		
		return scope;
	}
}
